import type { Ticket, Priority } from '../types/ticket';

// Urgency scoring - P1 always beats P2 beats P3 etc, no exceptions.
// Within each band: older tickets float up, SLA pressure adds a small bump.
// Bands are 1000pts apart so they never overlap.

const PRIORITY_BASE_SCORES: Record<Priority, number> = {
  P1: 4000,
  P2: 3000,
  P3: 2000,
  P4: 1000,
};

// +2 pts per minute aged, capped at 900 so we stay in our priority band
const AGE_WEIGHT = 2;
const AGE_BONUS_CAP = 900;

function calculateAgeBonus(createdAt: Date): number {
  const ageInMinutes = (Date.now() - new Date(createdAt).getTime()) / (1000 * 60);
  return Math.min(Math.floor(ageInMinutes * AGE_WEIGHT), AGE_BONUS_CAP);
}

function calculateSLABonus(slaDeadline: Date | null): number {
  if (!slaDeadline) return 0;

  const minutesRemaining = (new Date(slaDeadline).getTime() - Date.now()) / (1000 * 60);

  if (minutesRemaining <= 0) return 200;  // already breached
  if (minutesRemaining <= 30) return 150;
  if (minutesRemaining <= 60) return 100;
  return 0;
}

// Raw score for sorting (higher = more urgent)
export function calculateRawUrgencyScore(ticket: Ticket): number {
  return PRIORITY_BASE_SCORES[ticket.priority]
    + calculateAgeBonus(ticket.createdAt)
    + calculateSLABonus(ticket.slaDeadline);
}

// Normalized 0-100 score for display in the UI
export function calculatePriorityScore(ticket: Ticket): number {
  const MAX_RAW_SCORE = 5100; // P1 base + max age + max SLA
  return Math.round((calculateRawUrgencyScore(ticket) / MAX_RAW_SCORE) * 100);
}

export function getPriorityLevel(score: number) {
  if (score >= 86) return { level: 'critical' as const, label: 'Critical', color: '#dc2626' };
  if (score >= 61) return { level: 'high' as const, label: 'High', color: '#ea580c' };
  if (score >= 31) return { level: 'medium' as const, label: 'Medium', color: '#ca8a04' };
  return { level: 'low' as const, label: 'Low', color: '#65a30d' };
}

export function sortTicketsByPriority(tickets: Ticket[]): Ticket[] {
  return [...tickets].sort((a, b) => calculateRawUrgencyScore(b) - calculateRawUrgencyScore(a));
}
